import { useState, useEffect } from 'react';
import supabase from '../supabaseClient';
import { SB_URL, SB_KEY } from '../services/supabase-api';
import { Inp } from './ui/FormControls';

const lineaVacia = () => ({ referencia: "", descripcion: "", cajas: "", palets: "", fechaCaducidad: "" });

const subirAlbaran = async (file, numeroAlbaran) => {
  try {
    const carpeta = numeroAlbaran.trim().replace(/[^a-zA-Z0-9_-]/g, "_");
    const ext = file.name.split(".").pop();
    const nombre = `internacional/${carpeta}/albaran_${Date.now()}.${ext}`;
    const r = await fetch(`${SB_URL}/storage/v1/object/Documentos-registros/${nombre}`, {
      method: "POST",
      headers: { "apikey": SB_KEY, "Authorization": `Bearer ${SB_KEY}`, "Content-Type": file.type },
      body: file
    });
    if (!r.ok) return null;
    return `${SB_URL}/storage/v1/object/Documentos-registros/${nombre}`;
  } catch { return null; }
};

// ── OPERARIO (INTERNACIONAL): ENTRADAS CAMIONES ───────────────────
export const EntradasSection = ({ currentUser }) => {
  const [transporte, setTransporte] = useState({ numeroAlbaran: "", fecha: new Date().toISOString().slice(0, 10), proveedor: "", transportista: "", matricula: "", remolque: "", archivo: null, preview: null });
  const [lineas, setLineas] = useState([lineaVacia()]);
  const [proveedores, setProveedores] = useState([]);
  const [stockList, setStockList] = useState([]);
  const referenciasUnicas = [...new Map(stockList.map(s => [s.referencia, s])).values()];
  const [confirmando, setConfirmando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [exito, setExito] = useState(false);

  useEffect(() => {
    const almacen_id = currentUser?.almacen_id;
    if (!almacen_id) return;
    supabase.from('movimientos_stock').select('proveedor').eq('almacen_id', almacen_id).not('proveedor', 'is', null)
      .then(({ data }) => setProveedores([...new Set((data || []).map(d => d.proveedor).filter(Boolean))].sort()))
      .catch(() => {});
    supabase.from('stock').select('referencia, descripcion').eq('almacen_id', almacen_id)
      .then(({ data }) => setStockList(data || []))
      .catch(() => {});
  }, [currentUser?.almacen_id]);

  const actualizarLinea = (idx, campo, valor) => {
    setLineas(ls => ls.map((l, i) => {
      if (i !== idx) return l;
      const next = { ...l, [campo]: valor };
      if (campo === "referencia") {
        const match = stockList.find(s => s.referencia === valor);
        if (match) next.descripcion = match.descripcion || "";
      }
      return next;
    }));
  };

  const añadirLinea = () => setLineas(ls => [...ls, lineaVacia()]);
  const quitarLinea = (idx) => setLineas(ls => ls.filter((_, i) => i !== idx));

  const lineaIniciada = l => l.referencia.trim() || Number(l.cajas) > 0 || Number(l.palets) > 0;
  const lineaCompleta = l => l.referencia.trim() && l.fechaCaducidad && (Number(l.cajas) > 0 || Number(l.palets) > 0);
  const lineasValidas = lineas.filter(lineaCompleta);
  const hayLineaIncompleta = lineas.some(l => lineaIniciada(l) && !lineaCompleta(l));
  const formValido = transporte.numeroAlbaran.trim() && lineasValidas.length > 0 && !hayLineaIncompleta;

  const confirmarEntrada = async () => {
    setGuardando(true);
    try {
      const almacen_id = currentUser?.almacen_id;
      const usuario = currentUser?.user;
      const albaran_url = transporte.archivo ? await subirAlbaran(transporte.archivo, transporte.numeroAlbaran) : null;
      const matriculaFinal = transporte.remolque.trim()
        ? `${transporte.matricula.trim()} / ${transporte.remolque.trim()}`
        : transporte.matricula.trim();

      for (const l of lineasValidas) {
        const cajas = Number(l.cajas) || 0;
        const palets = Number(l.palets) || 0;
        const fecha_caducidad = l.fechaCaducidad || null;
        const referencia = l.referencia.trim();

        await supabase.from('movimientos_stock').insert({
          tipo: "entrada",
          referencia,
          cantidad_cajas: cajas,
          cantidad_palets: palets,
          fecha_caducidad,
          numero_albaran: transporte.numeroAlbaran.trim(),
          albaran_url,
          usuario,
          almacen_id,
          proveedor: transporte.proveedor.trim() || null,
          transportista: transporte.transportista.trim() || null,
          matricula: matriculaFinal || null,
        });

        let stockQuery = supabase.from('stock').select('*').eq('almacen_id', almacen_id).eq('referencia', referencia);
        stockQuery = fecha_caducidad ? stockQuery.eq('fecha_caducidad', fecha_caducidad) : stockQuery.is('fecha_caducidad', null);
        const { data: existente } = await stockQuery.maybeSingle();

        if (existente) {
          await supabase.from('stock').update({
            cantidad_cajas: (existente.cantidad_cajas || 0) + cajas,
            cantidad_palets: (existente.cantidad_palets || 0) + palets,
          }).eq('id', existente.id);
        } else {
          await supabase.from('stock').insert({
            almacen_id,
            referencia,
            descripcion: l.descripcion.trim(),
            fecha_caducidad,
            cantidad_cajas: cajas,
            cantidad_palets: palets,
          });
        }
      }

      setTransporte({ numeroAlbaran: "", fecha: new Date().toISOString().slice(0, 10), proveedor: "", transportista: "", matricula: "", remolque: "", archivo: null, preview: null });
      setLineas([lineaVacia()]);
      setConfirmando(false);
      setExito(true);
      setTimeout(() => setExito(false), 2500);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-4">
      {exito && (
        <div className="bg-green-50 border-2 border-green-400 text-green-700 rounded-xl py-6 text-center">
          <span className="text-xl font-semibold">✅ Entrada registrada correctamente</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <h2 className="font-bold text-gray-800 border-b pb-2">🚛 Entradas Camiones</h2>
        <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide">Datos del transporte</p>
        <Inp label="Nº albarán *" value={transporte.numeroAlbaran} onChange={v => setTransporte(s => ({ ...s, numeroAlbaran: v }))} placeholder="Nº de albarán" />
        <Inp label="Fecha" type="date" value={transporte.fecha} onChange={v => setTransporte(s => ({ ...s, fecha: v }))} />
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase">Proveedor</label>
          <input
            list="proveedores-conocidos"
            className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="Nombre del proveedor"
            value={transporte.proveedor}
            onChange={e => setTransporte(s => ({ ...s, proveedor: e.target.value }))}
          />
          <datalist id="proveedores-conocidos">{proveedores.map(p => <option key={p} value={p} />)}</datalist>
        </div>
        <Inp label="Transportista" value={transporte.transportista} onChange={v => setTransporte(s => ({ ...s, transportista: v }))} placeholder="Empresa de transporte" />
        <div className="grid grid-cols-2 gap-3">
          <Inp label="Matrícula" value={transporte.matricula} onChange={v => setTransporte(s => ({ ...s, matricula: v }))} placeholder="0000-AAA" />
          <Inp label="Remolque" value={transporte.remolque} onChange={v => setTransporte(s => ({ ...s, remolque: v }))} placeholder="Opcional" />
        </div>
        <div>
          <label className="text-xs font-semibold text-gray-600 uppercase">Foto del albarán</label>
          <label className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:bg-gray-50 transition-all">
            {transporte.preview ? (
              <img src={transporte.preview} alt="preview" className="max-h-48 rounded-lg object-contain" />
            ) : (
              <>
                <span className="text-3xl mb-2">📷</span>
                <span className="text-sm text-gray-500 font-semibold">Toca para hacer foto o seleccionar archivo</span>
                <span className="text-xs text-gray-400 mt-1">JPG, PNG, PDF — máx. 10MB</span>
              </>
            )}
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              className="hidden"
              onChange={e => {
                const f = e.target.files[0];
                if (!f) return;
                setTransporte(s => ({ ...s, archivo: f, preview: f.type.startsWith("image/") ? URL.createObjectURL(f) : null }));
              }}
            />
          </label>
          {transporte.archivo && <p className="text-xs text-green-600 font-semibold mt-1">✅ {transporte.archivo.name}</p>}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 space-y-3">
        <h2 className="font-bold text-gray-800 border-b pb-2">📦 Líneas de producto</h2>
        {lineas.map((l, idx) => (
          <div key={idx} className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold text-gray-500 uppercase">Línea {idx + 1}</p>
              {lineas.length > 1 && <button onClick={() => quitarLinea(idx)} className="text-xs text-red-500 underline">Quitar</button>}
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-600 uppercase">Referencia</label>
              <input
                list="stock-referencias"
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Referencia"
                value={l.referencia}
                onChange={e => actualizarLinea(idx, "referencia", e.target.value)}
              />
            </div>
            <Inp label="Descripción" value={l.descripcion} onChange={v => actualizarLinea(idx, "descripcion", v)} placeholder="Descripción del producto" />
            <div className="grid grid-cols-2 gap-3">
              <Inp label="Cajas" type="number" value={l.cajas} onChange={v => actualizarLinea(idx, "cajas", v)} placeholder="0" />
              <Inp label="Palets" type="number" value={l.palets} onChange={v => actualizarLinea(idx, "palets", v)} placeholder="0" />
            </div>
            <Inp label="Fecha caducidad *" type="date" value={l.fechaCaducidad} onChange={v => actualizarLinea(idx, "fechaCaducidad", v)} />
            {lineaIniciada(l) && !l.fechaCaducidad && (
              <p className="text-xs text-red-600 font-semibold">⚠️ Falta la fecha de caducidad de esta línea</p>
            )}
          </div>
        ))}
        <datalist id="stock-referencias">{referenciasUnicas.map(s => <option key={s.referencia} value={s.referencia} />)}</datalist>
        <button onClick={añadirLinea} className="w-full border-2 border-dashed border-gray-300 rounded-xl py-2 text-sm font-semibold text-gray-500 hover:bg-gray-50 transition-all">
          + Añadir línea
        </button>
      </div>

      {hayLineaIncompleta && (
        <p className="text-xs text-red-600 font-semibold text-center">⚠️ Completa la fecha de caducidad de todas las líneas antes de continuar</p>
      )}

      <button
        onClick={() => setConfirmando(true)}
        disabled={!formValido}
        className="w-full bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all"
      >
        Revisar y confirmar →
      </button>

      {confirmando && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-4 space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="font-bold text-gray-800 border-b pb-2">✅ Confirmar entrada</h2>
            <p className="text-xs text-gray-500">Albarán {transporte.numeroAlbaran} — esto se sumará al stock:</p>
            <div className="space-y-2">
              {lineasValidas.map((l, i) => (
                <div key={i} className="border rounded-lg p-3">
                  <p className="font-semibold text-gray-800 text-sm">{l.referencia}{l.descripcion ? ` — ${l.descripcion}` : ""}</p>
                  <p className="text-xs text-gray-600">+{Number(l.cajas) || 0} cajas · +{Number(l.palets) || 0} palets{l.fechaCaducidad ? ` · caduca ${l.fechaCaducidad}` : ""}</p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setConfirmando(false)} disabled={guardando} className="flex-1 border border-gray-300 text-gray-600 font-bold py-3 rounded-xl hover:bg-gray-50 disabled:opacity-40 transition-all">
                ← Volver
              </button>
              <button onClick={confirmarEntrada} disabled={guardando} className="flex-1 bg-cyan-600 text-white font-bold py-3 rounded-xl hover:bg-cyan-700 disabled:opacity-40 transition-all">
                {guardando ? "⏳ Guardando..." : "Confirmar →"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
